import './campaign_editor.scss';

import { bool, func } from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { Col, Row } from 'reactstrap';

import CategoryContext from '../../generic/CategorySelect/CategoryContext';
import DiscardableForm from '../../generic/DiscardableForm';
import ScopedDrawer, { changelog, comments } from '../../generic/ScopedDrawer';
import { billableBudgetShape, detailedCampaignShape } from '../../prop-types';
import FEATURE_FLAGS, { useSafeFlag } from '../../utils/flags';
import CampaignForm from '../CampaignForm';
import CampaignHeader from '../CampaignHeader/CampaignHeader.container';
import ConnectedCampaignLineFilters from '../CampaignLineFilters';
import CampaignLinesTableHeader from '../CampaignLinesTableHeader.container';
import CampaignLineStatusBar from '../CampaignLineStatusBar';
import CampaignLineTable from '../CampaignLineTable';

const NAVBAR_HEIGHT = 51;

function CampaignEditor({
  model,
  billableBudgets,
  onChange,
  onSubmit,
  refreshCampaignLineCreatives,
  isChangeRequestAllowed,
  isDelayReasonVisible,
  changeDelayReasonVisibility,
  isPushedToCCP,
  isPushedToCF,
  hasHPTOSelected,
}) {
  const { opportunity, ownerId, collaboratorIds } = model;
  const isDraftCampaignEnabled = useSafeFlag(FEATURE_FLAGS.DRAFT_CAMPAIGN);

  const stickyHeaderRef = useRef(null);
  const [stickyHeaderHeight, setStickyHeaderHeight] = useState(null);

  useEffect(() => {
    const el = stickyHeaderRef.current;
    if (!el) return undefined;
    const observer = new ResizeObserver(() => {
      setStickyHeaderHeight(el.offsetHeight);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <DiscardableForm
      onSubmit={onSubmit}
      className="c-campaign-editor wide-container-fluid"
    >
      <div
        ref={stickyHeaderRef}
        className="sticky-top c-secondary-sticky-navbar pt-3 mt-n3 bg-white"
      >
        <CampaignHeader
          campaignSave={onSubmit}
          campaign={model}
          isChangeRequestAllowed={isChangeRequestAllowed}
          onCreativeModalClose={refreshCampaignLineCreatives}
        />
        <CampaignLineStatusBar />
      </div>
      <div className="mb-4">
        <ScopedDrawer
          options={[
            comments({
              contentType: 'opportunity',
              objectId: opportunity.id,
              preferableMentions: [ownerId].concat(collaboratorIds),
            }),
            changelog({
              contentType: 'campaign',
              objectId: model.id,
            }),
          ]}
        >
          <CampaignForm
            model={model}
            onChange={onChange}
            billableBudgets={billableBudgets}
            isDelayReasonVisible={isDelayReasonVisible}
            changeDelayReasonVisibility={changeDelayReasonVisibility}
            isPushedToCCP={isPushedToCCP}
            isPushedToCF={isDraftCampaignEnabled ? false : isPushedToCF}
            hasHPTOSelected={hasHPTOSelected}
          />
        </ScopedDrawer>
      </div>

      <div
        className="py-2 bg-white sticky-top c-campaign-sticky-level-2"
        style={
          stickyHeaderHeight != null
            ? { top: NAVBAR_HEIGHT + stickyHeaderHeight }
            : undefined
        }
      >
        <Row className="mb-2">
          <Col sm={12}>
            <CategoryContext.Consumer>
              {categories => (
                <ConnectedCampaignLineFilters allCategories={categories} />
              )}
            </CategoryContext.Consumer>
          </Col>
        </Row>
        <Row>
          <Col>
            <CampaignLinesTableHeader campaignId={model.id} />
          </Col>
        </Row>
      </div>

      <CampaignLineTable
        campaignLines={model.campaignLines}
        onChange={onChange}
        campaignSave={onSubmit}
      />
    </DiscardableForm>
  );
}

CampaignEditor.propTypes = {
  model: detailedCampaignShape.isRequired,
  billableBudgets: billableBudgetShape.isRequired,
  onSubmit: func,
  onChange: func,
  refreshCampaignLineCreatives: func.isRequired,
  isChangeRequestAllowed: bool.isRequired,
  isDelayReasonVisible: bool.isRequired,
  changeDelayReasonVisibility: func,
  isPushedToCCP: bool.isRequired,
  isPushedToCF: bool.isRequired,
  hasHPTOSelected: bool.isRequired,
};

CampaignEditor.defaultProps = {
  onSubmit: undefined,
  onChange: undefined,
  changeDelayReasonVisibility: undefined,
};

export default CampaignEditor;
